import { ArrowLeft, Globe, Bell, Palette, Database } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useUserPreferences } from '@/hooks/useUserPreferences';
import { toast } from 'sonner';

const SystemPreferences = () => {
  const { t, i18n } = useTranslation();
  const { preferences, updatePreferences, resetToDefaults, isLoading, isUpdating, isResetting } = useUserPreferences();

  const handleLanguageChange = async (languageCode: string) => {
    try {
      await i18n.changeLanguage(languageCode);
      updatePreferences({ language_code: languageCode });
      toast.success(t('systemPreferences.success.languageChanged'));
    } catch (error) {
      toast.error('Failed to change language');
    }
  };

  const handlePreferenceChange = (key: string, value: any) => {
    updatePreferences({ [key]: value });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background p-4 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="container mx-auto max-w-4xl">
        <div className="mb-8">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4">
            <ArrowLeft className="h-4 w-4" />
            {t('systemPreferences.actions.backToDashboard')}
          </Link>
          <h1 className="heading-section mb-2">{t('systemPreferences.title')}</h1>
          <p className="text-muted-foreground">{t('systemPreferences.subtitle')}</p>
        </div>

        <div className="space-y-6">
          {/* Localization */}
          <Card className="glass-panel border-0">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                {t('systemPreferences.localization.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">{t('systemPreferences.localization.language')}</h4>
                  <p className="text-sm text-muted-foreground">{t('systemPreferences.localization.languageDescription')}</p>
                </div>
                <Select 
                  value={preferences?.language_code || 'en'} 
                  onValueChange={handleLanguageChange}
                >
                  <SelectTrigger className="w-48 glass-input">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">{t('languages.en')}</SelectItem>
                    <SelectItem value="no">{t('languages.no')}</SelectItem>
                    <SelectItem value="de">{t('languages.de')}</SelectItem>
                    <SelectItem value="fr">{t('languages.fr')}</SelectItem>
                    <SelectItem value="sv">{t('languages.sv')}</SelectItem>
                    <SelectItem value="da">{t('languages.da')}</SelectItem>
                    <SelectItem value="fi">{t('languages.fi')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">{t('systemPreferences.localization.timeZone')}</h4>
                  <p className="text-sm text-muted-foreground">{t('systemPreferences.localization.timeZoneDescription')}</p>
                </div>
                <Select 
                  value={preferences?.time_zone || 'utc'}
                  onValueChange={(value) => handlePreferenceChange('time_zone', value)}
                >
                  <SelectTrigger className="w-48 glass-input">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="utc">{t('timeZones.utc')}</SelectItem>
                    <SelectItem value="cet">{t('timeZones.cet')}</SelectItem>
                    <SelectItem value="est">{t('timeZones.est')}</SelectItem>
                    <SelectItem value="pst">{t('timeZones.pst')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">{t('systemPreferences.localization.dateFormat')}</h4>
                  <p className="text-sm text-muted-foreground">{t('systemPreferences.localization.dateFormatDescription')}</p>
                </div>
                <Select 
                  value={preferences?.date_format || 'iso'}
                  onValueChange={(value) => handlePreferenceChange('date_format', value)}
                >
                  <SelectTrigger className="w-48 glass-input">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="iso">
                      <div className="flex flex-col">
                        <span>ISO Format</span>
                        <span className="text-xs text-muted-foreground">2024-12-25</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="us">
                      <div className="flex flex-col">
                        <span>US Format</span>
                        <span className="text-xs text-muted-foreground">12/25/2024</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="eu">
                      <div className="flex flex-col">
                        <span>European Format</span>
                        <span className="text-xs text-muted-foreground">25/12/2024</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Notifications */}
          <Card className="glass-panel border-0">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                {t('systemPreferences.notifications.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">{t('systemPreferences.notifications.email')}</h4>
                  <p className="text-sm text-muted-foreground">{t('systemPreferences.notifications.emailDescription')}</p>
                </div>
                <Switch 
                  checked={preferences?.email_notifications ?? true}
                  onCheckedChange={(checked) => handlePreferenceChange('email_notifications', checked)}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">{t('systemPreferences.notifications.sms')}</h4>
                  <p className="text-sm text-muted-foreground">{t('systemPreferences.notifications.smsDescription')}</p>
                </div>
                <Switch 
                  checked={preferences?.sms_notifications ?? false}
                  onCheckedChange={(checked) => handlePreferenceChange('sms_notifications', checked)}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">{t('systemPreferences.notifications.desktop')}</h4>
                  <p className="text-sm text-muted-foreground">{t('systemPreferences.notifications.desktopDescription')}</p>
                </div>
                <Switch 
                  checked={preferences?.desktop_notifications ?? true}
                  onCheckedChange={(checked) => handlePreferenceChange('desktop_notifications', checked)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Data & Storage */}
          <Card className="glass-panel border-0">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                {t('systemPreferences.dataStorage.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">{t('systemPreferences.dataStorage.retention')}</h4>
                  <p className="text-sm text-muted-foreground">{t('systemPreferences.dataStorage.retentionDescription')}</p>
                </div>
                <Select 
                  value={preferences?.data_retention_period || '7years'}
                  onValueChange={(value) => handlePreferenceChange('data_retention_period', value)}
                >
                  <SelectTrigger className="w-48 glass-input">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1year">{t('dataRetention.1year')}</SelectItem>
                    <SelectItem value="3years">{t('dataRetention.3years')}</SelectItem>
                    <SelectItem value="5years">{t('dataRetention.5years')}</SelectItem>
                    <SelectItem value="7years">{t('dataRetention.7years')}</SelectItem>
                    <SelectItem value="indefinite">{t('dataRetention.indefinite')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">{t('systemPreferences.dataStorage.backups')}</h4>
                  <p className="text-sm text-muted-foreground">{t('systemPreferences.dataStorage.backupsDescription')}</p>
                </div>
                <Switch 
                  checked={preferences?.auto_backups ?? true}
                  onCheckedChange={(checked) => handlePreferenceChange('auto_backups', checked)}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">{t('systemPreferences.dataStorage.exportFormat')}</h4>
                  <p className="text-sm text-muted-foreground">{t('systemPreferences.dataStorage.exportFormatDescription')}</p>
                </div>
                <Select 
                  value={preferences?.export_format || 'pdf'}
                  onValueChange={(value) => handlePreferenceChange('export_format', value)}
                >
                  <SelectTrigger className="w-48 glass-input">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pdf">{t('exportFormats.pdf')}</SelectItem>
                    <SelectItem value="excel">{t('exportFormats.excel')}</SelectItem>
                    <SelectItem value="csv">{t('exportFormats.csv')}</SelectItem>
                    <SelectItem value="json">{t('exportFormats.json')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Interface */}
          <Card className="glass-panel border-0">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5" />
                {t('systemPreferences.interface.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">{t('systemPreferences.interface.sidebarCollapsed')}</h4>
                  <p className="text-sm text-muted-foreground">{t('systemPreferences.interface.sidebarCollapsedDescription')}</p>
                </div>
                <Switch 
                  checked={preferences?.sidebar_collapsed ?? false}
                  onCheckedChange={(checked) => handlePreferenceChange('sidebar_collapsed', checked)}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">{t('systemPreferences.interface.reducedMotion')}</h4>
                  <p className="text-sm text-muted-foreground">{t('systemPreferences.interface.reducedMotionDescription')}</p>
                </div>
                <Switch 
                  checked={preferences?.reduced_motion ?? false}
                  onCheckedChange={(checked) => handlePreferenceChange('reduced_motion', checked)}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">{t('systemPreferences.interface.highContrast')}</h4>
                  <p className="text-sm text-muted-foreground">{t('systemPreferences.interface.highContrastDescription')}</p>
                </div>
                <Switch 
                  checked={preferences?.high_contrast ?? false}
                  onCheckedChange={(checked) => handlePreferenceChange('high_contrast', checked)}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="mt-8 flex justify-end gap-4">
          <Button 
            variant="outline" 
            className="glass-button"
            onClick={resetToDefaults}
            disabled={isResetting}
          >
            {isResetting ? t('common.loading') : t('systemPreferences.actions.resetDefaults')}
          </Button>
          <Button 
            className="cta-primary"
            disabled={isUpdating}
          >
            {isUpdating ? t('common.loading') : t('systemPreferences.actions.savePreferences')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SystemPreferences;